import { ArrowLeft, ShieldCheck } from 'lucide-react'
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { useSessionStore } from '@/auth/sessionStore'
import { requestOtp, verifyOtp } from '@/auth/webSession'

import { Button } from '../components/ui/Button'
import { OtpInput } from '../components/ui/Controls'
import { Field } from '../components/ui/Field'
import { ErrorBanner } from '../components/ui/States'

export function LoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [phone, setPhone] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [devCode, setDevCode] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const verifying = useRef(false)
  const lastAutoCode = useRef('')
  const loadCurrentUser = useSessionStore((state) => state.loadCurrentUser)
  const next = params.get('next') ?? '/profile'

  const completeVerification = useCallback(
    async (activeChallenge: string, activeCode: string) => {
      if (verifying.current) return
      verifying.current = true
      setBusy(true)
      setError(null)
      try {
        await verifyOtp(activeChallenge, activeCode)
        await loadCurrentUser()
        navigate(next, { replace: true })
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : 'Please try again')
      } finally {
        verifying.current = false
        setBusy(false)
      }
    },
    [loadCurrentUser, navigate, next],
  )

  useEffect(() => {
    if (challengeId && code.length === 6 && lastAutoCode.current !== code) {
      lastAutoCode.current = code
      void completeVerification(challengeId, code)
    }
  }, [challengeId, code, completeVerification])

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (challengeId) {
      await completeVerification(challengeId, code)
      return
    }
    setBusy(true)
    setError(null)
    try {
      const challenge = await requestOtp(phone, 'customer')
      setChallengeId(challenge.challenge_id)
      setDevCode(challenge.dev_code ?? null)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Please try again')
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full flex-col md:my-6 md:min-h-[calc(100dvh-3rem)] md:max-w-105 md:overflow-hidden md:rounded-sheet md:border md:border-line/70 md:shadow-overlay">
      {/* Pine brand panel: the same editorial welcome the kitchen side gets */}
      <section className="relative overflow-hidden bg-pine px-5 pt-[max(0.75rem,var(--safe-top))] pb-8 text-white">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-16 -right-10 size-52 rounded-full bg-brand/40 blur-3xl"
        />
        <header className="relative flex items-center justify-between gap-3 py-2">
          <button
            type="button"
            aria-label="Go back"
            onClick={() => navigate(-1)}
            className="grid size-11 shrink-0 place-items-center rounded-control bg-white/10 text-white transition duration-200 select-none hover:bg-white/15 active:scale-95 motion-reduce:active:scale-100"
          >
            <ArrowLeft className="size-5" />
          </button>
          <span className="flex items-center gap-2.5">
            <img src="/icons/icon.svg" alt="" className="size-8" />
            <span className="font-display text-lg font-semibold tracking-tight">
              JDH Kitchen
            </span>
          </span>
          <span className="size-11" aria-hidden="true" />
        </header>
        <div className="stagger relative grid gap-2 pt-6">
          <span className="font-display text-[0.95rem] text-accent italic">
            {challengeId ? 'Almost there' : 'Welcome'}
          </span>
          <h1 className="max-w-[18ch] font-display text-title text-white">
            {challengeId ? 'Verify your phone' : 'Your kitchen, one tap away.'}
          </h1>
          <p className="max-w-prose text-sm text-white/80">
            {challengeId
              ? `Enter the six-digit OTP sent to +91 ${phone}. We’ll continue automatically.`
              : 'Sign in with your phone to order, manage plans and follow your deliveries.'}
          </p>
        </div>
      </section>
      <form
        onSubmit={(event) => void submit(event)}
        className="relative z-10 -mt-4 flex flex-1 flex-col gap-5 rounded-t-sheet bg-canvas px-5 pt-7 pb-[max(2rem,var(--safe-bottom))]"
      >
        <span className="text-overline text-brand uppercase">
          {challengeId ? 'Step 2 of 2' : 'Step 1 of 2'}
        </span>
        {!challengeId ? (
          <Field label="Mobile number">
            {(control) => (
              <div className="flex items-stretch overflow-hidden rounded-control border border-line-strong bg-surface transition focus-within:border-brand focus-within:ring-4 focus-within:ring-brand/12">
                <span className="grid place-items-center border-r border-line px-4 text-base font-bold text-ink-soft">
                  +91
                </span>
                <input
                  {...control}
                  autoFocus
                  inputMode="tel"
                  minLength={10}
                  maxLength={10}
                  value={phone}
                  onChange={(event) =>
                    setPhone(event.target.value.replace(/\D/g, ''))
                  }
                  className="min-h-13 w-full bg-transparent px-4 text-lg font-bold tracking-[0.08em] text-ink outline-none placeholder:text-ink-faint"
                />
              </div>
            )}
          </Field>
        ) : (
          <Field label="Six-digit OTP">
            {(control) => (
              <OtpInput
                {...control}
                autoFocus
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, ''))
                }
              />
            )}
          </Field>
        )}
        {devCode && challengeId && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setCode(devCode)}
          >
            Use local lab code {devCode}
          </Button>
        )}
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <Button
          type="submit"
          wide
          size="lg"
          busy={busy}
          disabled={!challengeId ? phone.length !== 10 : code.length !== 6}
        >
          {challengeId ? 'Verify OTP' : 'Send OTP'}
        </Button>
        {challengeId && (
          <Button
            variant="ghost"
            onClick={() => {
              setChallengeId(null)
              setCode('')
              setDevCode(null)
              lastAutoCode.current = ''
            }}
          >
            Change phone number
          </Button>
        )}
        <div className="mt-auto flex items-start gap-2.5 rounded-control bg-surface-tint p-4 text-sm text-ink-soft">
          <ShieldCheck
            className="size-4 shrink-0 text-success"
            aria-hidden="true"
          />
          <span>
            Your refresh session stays in a secure HttpOnly cookie and is never
            stored in browser storage.
          </span>
        </div>
      </form>
    </main>
  )
}
